import { Dialog, setDayWay, type DayLeg, type Plan, type PlanStop } from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { draftOf, wayOf, WayFields } from '../../components/way/WayFields';

interface Props {
  plan: Plan;
  stop: PlanStop;
  /** The day, and which of its own ways: to the room, back to it, or on. */
  day: string;
  leg: DayLeg;
  /** Where it goes from and to, by their names. */
  from: string;
  to: string;
  onClose: () => void;
  onSaved: (plan: Plan) => void;
}

/**
 * One of a day's own ways — from the airport to the room, back to it at night, from it to the
 * train on — said: by what and how long, or in parts with its changes; nothing said, walked.
 */
export function DayWayDialog({ plan, stop, day, leg, from, to, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const said = stop.dayWays.find((way) => way.day === day && way.leg === leg);
  const [way, setWay] = useState(() => draftOf(said));
  const [error, setError] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const sent = wayOf(way);
    if (typeof sent === 'string') {
      setError(t(`city.thing.${sent}`));
      return;
    }
    setError(undefined);
    setFailed(false);
    setDayWay(plan.trip.id, stop.id, [day, leg], sent).then(onSaved, () => {
      setFailed(true);
    });
  };

  return (
    <Dialog open onClose={onClose} title={t('way.widget', { from, to })}>
      <form className="form" onSubmit={submit} noValidate>
        <WayFields way={way} onWay={setWay} near={stop.place} end={to} error={error} />
        {failed && (
          <p className="form-failed" role="alert">
            {t('plan.failed')}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            {t('dialog.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('city.thing.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
